// hooks.ts - React Query hooks for Condition Parameters

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/hooks/use-toast";
import { conditionParametersApi, transformConditionParameter } from "./api";
import { ConditionParameter, FormData } from "./types";
import { convertToSnakeCase } from "@shared/utils/caseConverter";

// Get all condition parameters
export const useConditionParameters = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.ax.conditionParameters.list(), searchQuery],
    async () => {
      const response = await conditionParametersApi.getConditionParameters(
        utilityId,
        searchQuery
      );

      return {
        parameters: response.result.map(transformConditionParameter),
        totalWeightage: response.totalWeightage,
        isValidModel: response.isValidModel,
      };
    },
    {
      enabled: !!utilityId,
    }
  );
};

// Create condition parameter
export const useCreateConditionParameter = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  const mutation = useSmartMutation(
    async (data: { name: string; weightScore: number; description }) => {
      const payload = {
        name: data.name.trim(),
        score: data.weightScore,
        remoteUtilityId: utilityId,
        code: data.name.trim(),
        description: data.description.trim(),
      };

      return conditionParametersApi.createConditionParameter(payload);
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.ax.conditionParameters.list()],
        });
        toast({
          title: "Success",
          description: "Condition parameter created successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "Failed to create condition parameter",
          variant: "destructive",
        });
      },
    }
  );

  return {
    ...mutation,
    reset: mutation.reset,
  };
};

// Update condition parameter
export const useUpdateConditionParameter = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  const mutation = useSmartMutation(
    async (data: {
      code: string;
      name: string;
      weightScore: number;
      description: string;
    }) => {
      const payload = {
        name: data.name.trim(),
        score: data.weightScore,
        code: data.code,
        remoteUtilityId: utilityId,
        description: data.description.trim(),
      };

      return conditionParametersApi.updateConditionParameter(payload);
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.ax.conditionParameters.list()],
        });
        toast({
          title: "Success",
          description: "Condition parameter updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "Failed to update condition parameter",
          variant: "destructive",
        });
      },
    }
  );

  return {
    ...mutation,
    reset: mutation.reset,
  };
};

// Delete condition parameter
export const useDeleteConditionParameter = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { code: string; name: string }) => {
      const payload = {
        code: data.code,
        remoteUtilityId: utilityId,
      };

      return conditionParametersApi.deleteConditionParameter(payload);
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.ax.conditionParameters.list()],
        });
        toast({
          title: "Success",
          description: `${variables.name} has been deleted successfully`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "Failed to delete condition parameter",
          variant: "destructive",
        });
      },
    }
  );
};
